import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Card, CardContent, CircularProgress, Alert, FormControl,
  InputLabel, Select, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Pagination, Chip, Tooltip, IconButton, Skeleton, ToggleButton, ToggleButtonGroup, Link,
  useMediaQuery, useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import api from '../../api/axios';
import AnalyticsExportDialog, { DatasetKey, PayoutExportPeriod } from './AnalyticsExportDialog';

type Period = 'week' | 'lifetime';
type AffiliateStatus = 'owed' | 'negative' | 'no_activity';
type PlayerStatus = 'earning' | 'negative' | 'no_activity';

interface AffiliatePayoutRow {
  affiliate_id: number;
  affiliate_code: string;
  affiliate_name: string | null;
  tenant_id: number;
  tenant_name: string | null;
  is_active: boolean;
  is_approved: boolean;
  commission_rate: number;
  commission_type: string | null;
  players_in_pool: number;
  active_players: number;
  registered_in_period: number;
  total_topups: number;
  total_withdrawals: number;
  net_ggr: number;
  commissionable_ggr: number;
  estimated_commission: number;
  status: AffiliateStatus;
}

interface AffiliatePayoutTotals {
  affiliates: number;
  owed: number;
  negative: number;
  no_activity: number;
  net_ggr: number;
  estimated_commission: number;
}

interface AffiliatePayoutsResponse {
  period: Period;
  start_date: string | null;
  end_date: string | null;
  totals: AffiliatePayoutTotals;
  items: AffiliatePayoutRow[];
  pagination: { page: number; page_size: number; total_items: number; total_pages: number };
}

interface AffiliatePayoutPlayerRow {
  user_id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  registered_at: string | null;
  registered_in_period: boolean;
  topups: number;
  withdrawals: number;
  ggr: number;
  commissionable_ggr: number;
  estimated_commission: number;
  status: PlayerStatus;
}

interface AffiliatePayoutDetailResponse {
  period: Period;
  start_date: string | null;
  end_date: string | null;
  affiliate: AffiliatePayoutRow;
  players: AffiliatePayoutPlayerRow[];
}

const PAGE_SIZE = 100;

type ChipColor = 'success' | 'error' | 'default';

const AFFILIATE_STATUS: Record<AffiliateStatus, { label: string; color: ChipColor; hint?: string }> = {
  owed: { label: 'Owed', color: 'success' },
  negative: {
    label: 'Negative', color: 'error',
    hint: 'Players won more than they bought in this period, so no commission is due.',
  },
  no_activity: {
    label: 'No activity', color: 'default',
    hint: "None of this affiliate's players bought in or cashed out this period.",
  },
};

const PLAYER_STATUS: Record<PlayerStatus, { label: string; color: ChipColor }> = {
  earning: { label: 'Earning', color: 'success' },
  negative: { label: 'Negative', color: 'error' },
  no_activity: { label: 'No activity', color: 'default' },
};

const COMMISSION_TIP = "Commission % × each player's buy-ins minus cashouts. A player who cashed out more than they bought in counts as $0.";
const NET_TIP = "Buy-ins minus cashouts for all of this affiliate's players. Can be negative when players win.";
const NEGATIVE_PLAYER_HINT = 'Cashed out more than bought in, so this player counts as $0';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(amount || 0);

// $1,234.56, or −$1,029.60 (true minus sign, never parentheses).
const formatMoney = (amount: number) =>
  amount < 0 ? `−${formatCurrency(Math.abs(amount))}` : formatCurrency(amount);

const formatRate = (rate: number) => `${Number(rate || 0)}%`;

// Calendar dates (YYYY-MM-DD) are handled as UTC midnights so no local offset shifts the day.
const parseDay = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 86400000);
const toDay = (d: Date) => d.toISOString().slice(0, 10);

const formatDay = (s: string) =>
  new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
    .format(parseDay(s))
    .replace(',', '');

const formatWeekRange = (start: string, end: string) => `${formatDay(start)} – ${formatDay(end)}`;

// Monday of the current payout week, by today's date in US Eastern.
const currentEasternMonday = () => {
  const today = parseDay(new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date()));
  return addDays(today, -((today.getUTCDay() + 6) % 7));
};

// registered_at is UTC without a suffix; show it in the viewer's local time.
const formatLocal = (iso: string | null) => {
  if (!iso) return '';
  const normalized = /Z$|[+-]\d{2}:\d{2}$/.test(iso) ? iso : iso + 'Z';
  return new Date(normalized).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const playerName = (p: AffiliatePayoutPlayerRow) =>
  `${(p.first_name || '').trim()} ${(p.last_name || '').trim()}`.trim() || p.email || '—';

const Money: React.FC<{ value: number; bold?: boolean }> = ({ value, bold }) => (
  <Box component="span" sx={{ color: value < 0 ? 'error.main' : undefined, fontWeight: bold ? 700 : undefined }}>
    {formatMoney(value)}
  </Box>
);

const HeaderTip: React.FC<{ label: string; tip: string }> = ({ label, tip }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    {label}
    <Tooltip title={tip}>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
    </Tooltip>
  </Box>
);

const AffiliateBadges: React.FC<{ row: AffiliatePayoutRow }> = ({ row }) => (
  <>
    {!row.is_active && <Chip label="Inactive" size="small" variant="outlined" sx={{ ml: 1 }} />}
    {!row.is_approved && <Chip label="Not approved" size="small" variant="outlined" color="warning" sx={{ ml: 1 }} />}
    {row.commission_type === 'cpa' && <Chip label="CPA" size="small" variant="outlined" color="info" sx={{ ml: 1 }} />}
  </>
);

interface PayoutQuery {
  period: Period;
  start_date?: string;
  end_date?: string;
  tenant_id?: number;
}

// ---------------------------------------------------------------------------
// Drill-down: one affiliate's player pool for the same period.
// ---------------------------------------------------------------------------

interface AffiliatePayoutDetailProps {
  affiliateId: number;
  query: PayoutQuery;
  refreshKey: number;
  compact: boolean;
}

const AffiliatePayoutDetail: React.FC<AffiliatePayoutDetailProps> = ({ affiliateId, query, refreshKey, compact }) => {
  const [detail, setDetail] = useState<AffiliatePayoutDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showIdle, setShowIdle] = useState(false);

  const { period, start_date, end_date, tenant_id } = query;

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/api/v1/admin/analytics/affiliate-payouts/${affiliateId}`, {
          params: { period, start_date, end_date, tenant_id },
        });
        if (!cancelled) setDetail(res.data as AffiliatePayoutDetailResponse);
      } catch (err: any) {
        if (!cancelled) {
          setDetail(null);
          setError(err.response?.data?.detail || 'Failed to load affiliate players');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [affiliateId, period, start_date, end_date, tenant_id, refreshKey]);

  useEffect(() => { setShowIdle(false); }, [affiliateId]);

  if (loading && !detail) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>;
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!detail) return null;

  const aff = detail.affiliate;
  const active = detail.players.filter((p) => p.status !== 'no_activity');
  const idle = detail.players.filter((p) => p.status === 'no_activity');
  const hint = AFFILIATE_STATUS[aff.status]?.hint;

  const renderPlayer = (p: AffiliatePayoutPlayerRow) => {
    const meta = PLAYER_STATUS[p.status] || PLAYER_STATUS.no_activity;
    return (
      <TableRow key={p.user_id}>
        <TableCell>
          {playerName(p)}
          {p.registered_at && (
            <Typography variant="caption" color="textSecondary" display="block">
              Signed up {formatLocal(p.registered_at)}
            </Typography>
          )}
          {p.status === 'negative' && (
            <Typography variant="caption" color="error" display="block">{NEGATIVE_PLAYER_HINT}</Typography>
          )}
        </TableCell>
        <TableCell>{p.phone_number || '—'}</TableCell>
        <TableCell><Chip label={meta.label} color={meta.color} size="small" /></TableCell>
        <TableCell align="right">{formatMoney(p.topups)}</TableCell>
        <TableCell align="right">{formatMoney(p.withdrawals)}</TableCell>
        <TableCell align="right"><Money value={p.ggr} /></TableCell>
        <TableCell align="right"><Money value={p.estimated_commission} bold={p.estimated_commission > 0} /></TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ opacity: loading ? 0.6 : 1 }}>
      <Typography variant="subtitle1">
        {aff.affiliate_name || aff.affiliate_code}
        <Typography component="span" variant="caption" color="textSecondary" sx={{ ml: 1 }}>{aff.affiliate_code}</Typography>
        <AffiliateBadges row={aff} />
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
        {formatRate(aff.commission_rate)} · {aff.active_players}/{aff.players_in_pool} players active
        {compact && <> · Buy-ins {formatMoney(aff.total_topups)} · Cashouts {formatMoney(aff.total_withdrawals)}</>}
        {' · Net '}<Money value={aff.net_ggr} />
        {' · Est. commission '}<Money value={aff.estimated_commission} bold />
      </Typography>
      {hint && <Alert severity={aff.status === 'negative' ? 'warning' : 'info'} sx={{ mb: 1 }}>{hint}</Alert>}

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 760 }}>
          <TableHead>
            <TableRow>
              <TableCell>Player</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Buy-ins</TableCell>
              <TableCell align="right">Cashouts</TableCell>
              <TableCell align="right">Net result</TableCell>
              <TableCell align="right">Commission</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {active.map(renderPlayer)}
            {idle.length > 0 && !showIdle && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Link component="button" variant="body2" onClick={() => setShowIdle(true)}>
                    + {idle.length} more player{idle.length === 1 ? '' : 's'} with no activity
                  </Link>
                </TableCell>
              </TableRow>
            )}
            {showIdle && idle.map(renderPlayer)}
            {detail.players.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">No players have signed up with this affiliate yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

interface AffiliatePayoutsSectionProps {
  // Set only for super admins; tenant admins are scoped by their token.
  tenantId?: number;
  // Super admin viewing all tenants: show the Tenant column.
  showTenantColumn: boolean;
  onError: (message: string) => void;
}

const AffiliatePayoutsSection: React.FC<AffiliatePayoutsSectionProps> = ({ tenantId, showTenantColumn, onError }) => {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('sm'));

  const [period, setPeriod] = useState<Period>('week');
  const [weekOffset, setWeekOffset] = useState(0);
  const [status, setStatus] = useState<AffiliateStatus | ''>('');
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  const [data, setData] = useState<AffiliatePayoutsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const [exportOpen, setExportOpen] = useState(false);
  const [exportDataset, setExportDataset] = useState<DatasetKey>('affiliate_payouts');

  // Current week sends no dates; past weeks send their Monday–Sunday range.
  const buildQuery = useCallback((): PayoutQuery => {
    const q: PayoutQuery = { period };
    if (period === 'week' && weekOffset !== 0) {
      const monday = addDays(currentEasternMonday(), weekOffset * 7);
      q.start_date = toDay(monday);
      q.end_date = toDay(addDays(monday, 6));
    }
    if (tenantId) q.tenant_id = tenantId;
    return q;
  }, [period, weekOffset, tenantId]);

  const query = buildQuery();

  // A new period, week or tenant starts from page 1 with the drill-down closed.
  useEffect(() => {
    setPage(1);
    setExpandedId(null);
  }, [period, weekOffset, tenantId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/api/v1/admin/analytics/affiliate-payouts', {
          params: { ...buildQuery(), status: status || undefined, page, page_size: PAGE_SIZE },
        });
        if (!cancelled) setData(res.data as AffiliatePayoutsResponse);
      } catch (err: any) {
        if (!cancelled) {
          const detail = err.response?.data?.detail;
          setError(typeof detail === 'string' ? detail : 'Failed to load affiliate payouts');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [buildQuery, status, page, refreshKey]);

  const changeStatus = (next: AffiliateStatus | '') => {
    setStatus(next);
    setPage(1);
  };

  const openExport = (dataset: DatasetKey) => {
    setExportDataset(dataset);
    setExportOpen(true);
  };

  // Export exactly the period on screen (the API's resolved range).
  const exportPeriod: PayoutExportPeriod = {
    period,
    start_date: period === 'week' ? data?.start_date ?? query.start_date ?? null : null,
    end_date: period === 'week' ? data?.end_date ?? query.end_date ?? null : null,
  };

  const totals = data?.totals;
  const items = data?.items ?? [];
  const showTenant = showTenantColumn && !tenantId;
  const showSignups = period === 'week';
  const columnCount = compact ? 3 : 8 + (showTenant ? 1 : 0) + (showSignups ? 1 : 0);
  // The week label only reflects the response once it matches the period on screen.
  const shownWeek = data && data.period === 'week' && data.start_date && data.end_date
    ? formatWeekRange(data.start_date, data.end_date)
    : null;

  const countCards: { key: AffiliateStatus; label: string; value?: number }[] = [
    { key: 'owed', label: 'Owed', value: totals?.owed },
    { key: 'negative', label: 'Negative', value: totals?.negative },
    { key: 'no_activity', label: 'No activity', value: totals?.no_activity },
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flexGrow: 1, minWidth: 240 }}>
          <Typography variant="h6">Affiliate Payouts</Typography>
          <Typography variant="body2" color="textSecondary">
            Live estimate. Updates every time you open or refresh this page. Payouts are manual.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => openExport('affiliate_payouts')}>
            Export affiliates
          </Button>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => openExport('affiliate_payout_players')}>
            Export players
          </Button>
        </Box>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={period}
          onChange={(_e, v: Period | null) => { if (v) setPeriod(v); }}
        >
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="lifetime">Lifetime</ToggleButton>
        </ToggleButtonGroup>

        {period === 'week' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" aria-label="Previous week" onClick={() => setWeekOffset((o) => o - 1)}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" sx={{ minWidth: 260, textAlign: 'center' }}>
              {shownWeek ? `Payout week: ${shownWeek} (Eastern)` : 'Payout week: …'}
            </Typography>
            <IconButton
              size="small"
              aria-label="Next week"
              disabled={weekOffset === 0}
              onClick={() => setWeekOffset((o) => Math.min(0, o + 1))}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        ) : (
          <Typography variant="body2">Lifetime: all activity since each player signed up</Typography>
        )}

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => changeStatus(e.target.value as AffiliateStatus | '')}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="owed">Owed</MenuItem>
            <MenuItem value="negative">Negative</MenuItem>
            <MenuItem value="no_activity">No activity</MenuItem>
          </Select>
        </FormControl>

        <Button size="small" startIcon={<RefreshIcon />} onClick={() => setRefreshKey((k) => k + 1)} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {/* Summary cards (all affiliates, ignore the status filter) */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Card sx={{ flex: '1 1 160px' }}>
          <CardContent>
            <Typography color="textSecondary" variant="body2" gutterBottom>
              <HeaderTip label="Est. commission" tip={COMMISSION_TIP} />
            </Typography>
            <Typography variant="h5">{totals ? formatMoney(totals.estimated_commission) : '—'}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 160px' }}>
          <CardContent>
            <Typography color="textSecondary" variant="body2" gutterBottom>
              <HeaderTip label="Net result" tip={NET_TIP} />
            </Typography>
            <Typography variant="h5">{totals ? <Money value={totals.net_ggr} /> : '—'}</Typography>
          </CardContent>
        </Card>
        {countCards.map((c) => {
          const selected = status === c.key;
          return (
            <Card
              key={c.key}
              onClick={() => changeStatus(selected ? '' : c.key)}
              sx={{
                flex: '1 1 120px',
                cursor: 'pointer',
                outline: selected ? `2px solid ${theme.palette.primary.main}` : 'none',
              }}
            >
              <CardContent>
                <Typography color="textSecondary" variant="body2" gutterBottom>{c.label}</Typography>
                <Typography variant="h5">{c.value ?? '—'}</Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Affiliate table */}
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: compact ? undefined : 980 }}>
          <TableHead>
            <TableRow>
              <TableCell>Affiliate</TableCell>
              {!compact && showTenant && <TableCell>Tenant</TableCell>}
              <TableCell>Status</TableCell>
              {!compact && <TableCell align="right">%</TableCell>}
              {!compact && <TableCell align="right">Active / all</TableCell>}
              {!compact && showSignups && <TableCell align="right">Signups</TableCell>}
              {!compact && <TableCell align="right">Buy-ins</TableCell>}
              {!compact && <TableCell align="right">Cashouts</TableCell>}
              {!compact && <TableCell align="right"><HeaderTip label="Net result" tip={NET_TIP} /></TableCell>}
              <TableCell align="right"><HeaderTip label="Est. commission" tip={COMMISSION_TIP} /></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && !data && Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={`skeleton-${i}`}>
                {Array.from({ length: columnCount }).map((__, j) => (
                  <TableCell key={j}><Skeleton /></TableCell>
                ))}
              </TableRow>
            ))}
            {items.map((row) => {
              const meta = AFFILIATE_STATUS[row.status] || AFFILIATE_STATUS.no_activity;
              const expanded = expandedId === row.affiliate_id;
              return (
                <TableRow
                  key={row.affiliate_id}
                  hover
                  selected={expanded}
                  onClick={() => setExpandedId(expanded ? null : row.affiliate_id)}
                  sx={{ cursor: 'pointer', opacity: loading ? 0.6 : 1 }}
                >
                  <TableCell>
                    {row.affiliate_name || row.affiliate_code}
                    <AffiliateBadges row={row} />
                    <Typography variant="caption" color="textSecondary" display="block">{row.affiliate_code}</Typography>
                  </TableCell>
                  {!compact && showTenant && <TableCell>{row.tenant_name || row.tenant_id}</TableCell>}
                  <TableCell>
                    <Tooltip title={meta.hint || ''}>
                      <Chip label={meta.label} color={meta.color} size="small" />
                    </Tooltip>
                  </TableCell>
                  {!compact && <TableCell align="right">{formatRate(row.commission_rate)}</TableCell>}
                  {!compact && <TableCell align="right">{row.active_players} / {row.players_in_pool}</TableCell>}
                  {!compact && showSignups && <TableCell align="right">{row.registered_in_period}</TableCell>}
                  {!compact && <TableCell align="right">{formatMoney(row.total_topups)}</TableCell>}
                  {!compact && <TableCell align="right">{formatMoney(row.total_withdrawals)}</TableCell>}
                  {!compact && <TableCell align="right"><Money value={row.net_ggr} /></TableCell>}
                  <TableCell align="right">
                    <Money value={row.estimated_commission} bold={row.estimated_commission > 0} />
                  </TableCell>
                </TableRow>
              );
            })}
            {data && !items.length && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center">
                  {data.totals.affiliates === 0
                    ? 'No affiliates yet. Approve affiliate applications to see them here.'
                    : 'No affiliates match this filter for the selected period.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {data && data.pagination.total_pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination count={data.pagination.total_pages} page={page} onChange={(_e, p) => setPage(p)} />
        </Box>
      )}

      {/* Player drill-down */}
      {expandedId !== null && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <AffiliatePayoutDetail affiliateId={expandedId} query={query} refreshKey={refreshKey} compact={compact} />
        </Paper>
      )}

      <AnalyticsExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        startDate=""
        endDate=""
        tenantId={tenantId}
        onError={onError}
        initialDataset={exportDataset}
        payout={exportPeriod}
      />
    </Box>
  );
};

export default AffiliatePayoutsSection;
